import { useEffect, useMemo, useState } from 'preact/hooks';
import { api } from '../../lib/api.js';
import {
  deriveFromCmyk,
  deriveFromHex,
  deriveFromLab,
  deriveFromRgb,
  isLabInRgbGamut,
  rgbToCmykog,
} from '../../lib/colour.js';
import { SaveInkModal } from './SaveInkModal.jsx';

const DEFAULT = deriveFromLab(50, 0, 0); // neutral mid-grey
const PURPLE = '#A18AF5';

const NumberInput = ({ value, onCommit, min, max, step = 1, decimals = 0, ariaLabel }) => {
  const [text, setText] = useState(value.toFixed(decimals));
  const [focused, setFocused] = useState(false);
  // Sync external value into local text only when we're not actively typing,
  // so the parent's recomputed value doesn't fight our keystrokes.
  useEffect(() => {
    if (!focused) setText(value.toFixed(decimals));
  }, [value, decimals, focused]);
  return (
    <input
      type="number"
      value={text}
      min={min}
      max={max}
      step={step}
      aria-label={ariaLabel}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onInput={(e) => {
        const v = e.currentTarget.value;
        setText(v);
        const n = parseFloat(v);
        if (Number.isFinite(n)) onCommit(Math.max(min, Math.min(max, n)));
      }}
      class="w-full rounded-md border border-border-strong bg-bg-base px-2.5 py-1.5 text-right font-serif text-[16px] font-medium tracking-tighter text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
    />
  );
};

const ChannelLabel = ({ label, color }) => (
  <span class="w-4 shrink-0 text-[12px] font-bold" style={{ color }}>{label}</span>
);

export const ConverterPage = ({ inkId: queryInkId }) => {
  // The four representations are kept in sync via the deriveFrom* helpers.
  const [current, setCurrent] = useState(DEFAULT);
  const [editing, setEditing] = useState('lab');
  const [hexText, setHexText] = useState(DEFAULT.hex);
  const [savingOpen, setSavingOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const [sourceInk, setSourceInk] = useState(null);

  // Deep-link entry: ?inkId=NNN pre-fills from an existing ink.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = queryInkId ?? params.get('inkId');
    if (!id) return;
    api.get(`/ink-books/inks/${id}`).then((ink) => {
      if (ink.targetLab) {
        const derived = deriveFromLab(ink.targetLab.l, ink.targetLab.a, ink.targetLab.b);
        setCurrent(derived);
        setHexText(derived.hex);
      } else if (ink.targetHex) {
        const derived = deriveFromHex(ink.targetHex);
        if (derived) { setCurrent(derived); setHexText(derived.hex); }
      }
      setSourceInk({ id: ink.id, name: ink.name, bookId: ink.bookId, pantoneCode: ink.pantoneCode });
    }).catch(() => {/* ignore — empty default state */});
  }, [queryInkId]);

  const inGamut = useMemo(
    () => isLabInRgbGamut(current.lab[0], current.lab[1], current.lab[2]),
    [current],
  );

  const update = (deriver) => (...args) => {
    const next = typeof deriver === 'function' ? deriver(...args) : null;
    if (!next) return;
    setCurrent(next);
    setHexText(next.hex);
  };

  const setLab = (i, v) => {
    const lab = [...current.lab];
    lab[i] = v;
    update(deriveFromLab)(lab[0], lab[1], lab[2]);
    setEditing('lab');
  };
  const setCmyk = (i, v) => {
    const cmyk = [...current.cmyk];
    cmyk[i] = v;
    update(deriveFromCmyk)(cmyk[0], cmyk[1], cmyk[2], cmyk[3]);
    setEditing('cmyk');
  };
  const setRgb = (i, v) => {
    const rgb = [...current.rgb];
    rgb[i] = v;
    update(deriveFromRgb)(rgb[0], rgb[1], rgb[2]);
    setEditing('rgb');
  };
  const setHex = (s) => {
    setHexText(s);
    const derived = deriveFromHex(s);
    if (derived) {
      setCurrent(derived);
      setEditing('hex');
    }
  };

  const clearAll = () => {
    setCurrent(DEFAULT);
    setHexText(DEFAULT.hex);
    setSourceInk(null);
    setEditing('lab');
  };

  const copyText = (label, text) => {
    navigator.clipboard?.writeText(text);
    // No toast for v1; silent copy
  };

  const handleSaved = (savedInk) => {
    setRecent((r) => [
      { ...current, savedInk, time: Date.now() },
      ...r.filter((x) => x.hex !== current.hex).slice(0, 9),
    ]);
    setSavingOpen(false);
    setSourceInk({
      id: savedInk.id,
      name: savedInk.name,
      bookId: savedInk.bookId,
      pantoneCode: savedInk.pantoneCode,
    });
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div>
      <section class="mb-6">
        <div class="mb-1.5 flex items-center gap-2">
          <span class="block h-2.5 w-2.5 rounded-full" style={{ background: PURPLE }} aria-hidden="true" />
          <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: PURPLE }}>
            Converter
          </span>
        </div>
        <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">
          Colour conversion
        </h1>
        <p class="m-0 text-sm text-text-secondary">
          Edit any space. The others update live. Save the result to an ink in any book.
        </p>
      </section>

      {/* Tabs (Compare disabled for v1) */}
      <div class="mb-5 flex gap-0 border-b border-border-subtle">
        <button class="border-b-2 border-status-purple px-5 py-2.5 text-[13px] font-semibold text-text-primary">
          Convert
        </button>
        <button
          disabled
          class="cursor-not-allowed border-b-2 border-transparent px-5 py-2.5 text-[13px] font-semibold text-text-tertiary"
          title="Requires X-Rite connection"
        >
          Compare <span class="ml-1.5 text-[10px] font-medium text-text-muted">requires X-Rite</span>
        </button>
      </div>

      {/* Swatch row */}
      <div class="mb-5 flex items-center gap-4 rounded-lg border border-border-subtle bg-bg-card p-4">
        <div
          class="h-[90px] w-[140px] shrink-0 rounded-lg border border-border-subtle"
          style={{ background: current.hex }}
        />
        <div class="min-w-0 flex-1">
          <p class="label mb-1">Current colour</p>
          {sourceInk ? (
            <>
              <p class="m-0 font-serif text-[20px] font-medium text-text-primary">
                {sourceInk.name}
              </p>
              <p class="m-0 font-mono text-[13px] text-text-tertiary">
                {current.hex} · LAB {current.lab.map((v) => v.toFixed(1)).join(', ')}
              </p>
            </>
          ) : (
            <>
              <p class="m-0 font-serif text-[20px] font-medium text-text-secondary">Untitled</p>
              <p class="m-0 font-mono text-[13px] text-text-tertiary">
                {current.hex} · LAB {current.lab.map((v) => v.toFixed(1)).join(', ')}
              </p>
            </>
          )}
          <div class="mt-1.5">
            {inGamut ? (
              <span class="inline-flex items-center gap-1.5 rounded-full border border-status-ok/[0.3] bg-status-ok/[0.12] px-2.5 py-0.5 text-[11px] font-semibold text-status-ok">
                <span class="block h-1.5 w-1.5 rounded-full bg-status-ok" /> In sRGB gamut
              </span>
            ) : (
              <span class="inline-flex items-center gap-1.5 rounded-full border border-ink-yellow/[0.3] bg-ink-yellow/[0.12] px-2.5 py-0.5 text-[11px] font-semibold text-ink-yellow">
                <span class="block h-1.5 w-1.5 rounded-full bg-ink-yellow" /> Out of sRGB gamut · clamped
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Three panels */}
      <div class="mb-5 grid grid-cols-1 gap-2.5 md:grid-cols-3">

        {/* LAB */}
        <div class="relative overflow-hidden rounded-md border border-border-subtle bg-bg-card p-4">
          {editing === 'lab' && (
            <span class="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-status-purple" aria-hidden="true" />
          )}
          <div class="mb-3 flex items-center justify-between">
            <span class="font-serif text-[17px] font-medium text-text-primary">LAB</span>
            <span class={`text-[10px] font-semibold uppercase tracking-eyebrow ${editing === 'lab' ? 'text-status-purple' : 'text-text-muted'}`}>
              {editing === 'lab' ? 'Editing' : 'Derived'}
            </span>
          </div>
          {[
            { label: 'L', min: 0, max: 100 },
            { label: 'a', min: -128, max: 128 },
            { label: 'b', min: -128, max: 128 },
          ].map((cfg, i) => (
            <div key={cfg.label} class="mb-2 flex items-center gap-2 last:mb-0">
              <ChannelLabel label={cfg.label} color="#A8A095" />
              <NumberInput
                value={current.lab[i]}
                onCommit={(v) => setLab(i, v)}
                min={cfg.min} max={cfg.max} step={0.1} decimals={1}
                ariaLabel={`LAB ${cfg.label}`}
              />
            </div>
          ))}
        </div>

        {/* CMYKOG */}
        <div class="relative overflow-hidden rounded-md border border-border-subtle bg-bg-card p-4">
          {editing === 'cmyk' && (
            <span class="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-status-purple" aria-hidden="true" />
          )}
          <div class="mb-3 flex items-center justify-between">
            <span class="font-serif text-[17px] font-medium text-text-primary">CMYKOG</span>
            <span class={`text-[10px] font-semibold uppercase tracking-eyebrow ${editing === 'cmyk' ? 'text-status-purple' : 'text-text-muted'}`}>
              {editing === 'cmyk' ? 'Editing' : 'Estimate'}
            </span>
          </div>
          {[
            { label: 'C', color: '#00B4D8' },
            { label: 'M', color: '#E63B7A' },
            { label: 'Y', color: '#F4C430' },
            { label: 'K', color: '#F5EEDC' },
          ].map((cfg, i) => (
            <div key={cfg.label} class="mb-2 flex items-center gap-2">
              <ChannelLabel label={cfg.label} color={cfg.color} />
              <NumberInput
                value={current.cmyk[i]}
                onCommit={(v) => setCmyk(i, v)}
                min={0} max={100} step={1} decimals={0}
                ariaLabel={`CMYK ${cfg.label}`}
              />
            </div>
          ))}
          {/* O and G are derived display only — no ICC profile to honour their inverse. */}
          <div class="mt-2 border-t border-border-subtle pt-2">
            {(() => {
              const [, , , , o, g] = rgbToCmykog(...current.rgb);
              return [
                { label: 'O', color: '#FF8A4C', value: o },
                { label: 'G', color: '#7BD4A8', value: g },
              ].map((cfg) => (
                <div key={cfg.label} class="mb-1 flex items-center gap-2 last:mb-0">
                  <ChannelLabel label={cfg.label} color={cfg.color} />
                  <div class="w-full rounded-md border border-border-subtle bg-bg-base px-2.5 py-1.5 text-right font-serif text-[16px] font-medium tracking-tighter text-text-tertiary">
                    {cfg.value}
                  </div>
                </div>
              ));
            })()}
            <p class="mt-2 text-[10px] font-medium text-text-muted">
              O · G are rough estimates only. Real recipes come from a scan + manual mix entry.
            </p>
          </div>
        </div>

        {/* RGB + hex */}
        <div class="relative overflow-hidden rounded-md border border-border-subtle bg-bg-card p-4">
          {(editing === 'rgb' || editing === 'hex') && (
            <span class="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-status-purple" aria-hidden="true" />
          )}
          <div class="mb-3 flex items-center justify-between">
            <span class="font-serif text-[17px] font-medium text-text-primary">RGB</span>
            <span class={`text-[10px] font-semibold uppercase tracking-eyebrow ${editing === 'rgb' || editing === 'hex' ? 'text-status-purple' : 'text-text-muted'}`}>
              {editing === 'rgb' || editing === 'hex' ? 'Editing' : 'Derived'}
            </span>
          </div>
          {[
            { label: 'R', color: '#E84545' },
            { label: 'G', color: '#5DB075' },
            { label: 'B', color: '#4A8EE0' },
          ].map((cfg, i) => (
            <div key={cfg.label} class="mb-2 flex items-center gap-2 last:mb-0">
              <ChannelLabel label={cfg.label} color={cfg.color} />
              <NumberInput
                value={current.rgb[i]}
                onCommit={(v) => setRgb(i, v)}
                min={0} max={255} step={1} decimals={0}
                ariaLabel={`RGB ${cfg.label}`}
              />
            </div>
          ))}
          <div class="mt-3 flex items-center gap-2 border-t border-border-subtle pt-3">
            <span class="w-8 shrink-0 text-[11px] font-bold tracking-wider text-text-tertiary">HEX</span>
            <input
              type="text"
              value={hexText}
              onInput={(e) => setHex(e.currentTarget.value)}
              maxlength={7}
              class="w-full rounded-md border border-border-strong bg-bg-base px-2.5 py-1.5 font-mono text-[14px] font-medium uppercase tracking-wider text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
        </div>

      </div>

      {/* Actions */}
      <div class="mb-8 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setSavingOpen(true)}
          class="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-bold text-[#1A0E04] hover:brightness-105"
        >
          ★ Save to ink
        </button>
        <button
          onClick={() => copyText('hex', current.hex)}
          class="rounded-md border border-border-strong bg-transparent px-4 py-2 text-sm font-medium text-text-body hover:bg-bg-card"
        >
          Copy hex
        </button>
        <button
          onClick={() => copyText('all', `LAB ${current.lab.map((v) => v.toFixed(1)).join(', ')}\nCMYK ${current.cmyk.join(', ')}\nRGB ${current.rgb.join(', ')}\nHEX ${current.hex}`)}
          class="rounded-md border border-border-strong bg-transparent px-4 py-2 text-sm font-medium text-text-body hover:bg-bg-card"
        >
          Copy all
        </button>
        <button
          onClick={clearAll}
          class="ml-auto rounded-md border border-border-strong bg-transparent px-4 py-2 text-sm font-medium text-text-body hover:bg-bg-card"
        >
          Clear
        </button>
      </div>

      {/* Recent */}
      {recent.length > 0 && (
        <section>
          <div class="mb-2 flex items-baseline justify-between">
            <h2 class="m-0 font-serif text-[17px] font-medium text-text-primary">Recent</h2>
            <span class="text-[12px] font-medium text-text-muted">Last {recent.length}</span>
          </div>
          <div class="overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
            {recent.map((r, idx) => (
              <div key={idx} class="flex items-center gap-3 border-b border-border-subtle px-4 py-2.5 last:border-b-0">
                <div class="h-[30px] w-[30px] shrink-0 rounded-md border border-border-subtle" style={{ background: r.hex }} />
                <div class="min-w-0 flex-1">
                  <p class="m-0 font-serif text-[14px] font-medium text-text-primary">
                    {r.savedInk?.name ?? 'Untitled'}
                  </p>
                  <p class="m-0 font-mono text-[11px] text-text-tertiary">
                    {r.hex} · LAB {r.lab.map((v) => v.toFixed(1)).join(', ')}
                  </p>
                </div>
                {r.savedInk && (
                  <span class="inline-flex items-center gap-1.5 rounded-full border border-brand/[0.3] bg-brand/[0.12] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand">
                    ★ Saved
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <SaveInkModal
        open={savingOpen}
        onClose={() => setSavingOpen(false)}
        onSaved={handleSaved}
        lab={current.lab}
        hex={current.hex}
        defaultInkId={sourceInk?.id}
      />
    </div>
  );
};
